import { LitElement, html, css } from 'lit';
import { api } from '../api.js';
import { confirmDialog } from '../utils/confirm-dialog.js';

/**
 * Bedrijfsprofiel (/settings/company). Upload/vervang/verwijder het bedrijfslogo.
 * Logo wordt als base64 data-URI in de companies-tabel bewaard (kolom `logo`).
 * Alleen owner/manager/sysadmin mag bewerken (server-side afgedwongen). De scangeluiden staan
 * op /settings/recorder (Recorder app), zie scan-sounds-card.js.
 */
export class CompanySettings extends LitElement {
  static properties = {
    _loading: { state: true },
    _saving: { state: true },
    _error: { state: true },
    _saved: { state: true },
    _logo: { state: true },
    _canEdit: { state: true },
  };

  constructor() {
    super();
    this._loading = true;
    this._saving = false;
    this._error = null;
    this._saved = false;
    this._logo = null;
    this._canEdit = false;
  }

  connectedCallback() { super.connectedCallback(); this._load(); }

  async _load() {
    this._loading = true;
    try {
      const res = await api.getCompanyProfile();
      this._logo = res?.logo ?? null;
      this._canEdit = !!res?.canEdit;
    } catch (e) {
      this._error = 'Laden mislukt';
    } finally {
      this._loading = false;
    }
  }

  _pickFile() { this.renderRoot.querySelector('#logo-input')?.click(); }

  async _onFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\/(png|jpe?g|svg\+xml|webp)$/.test(file.type)) {
      this._error = 'Kies een PNG, JPG, SVG of WebP.';
      return;
    }
    if (file.size > 500 * 1024) {
      this._error = 'Logo is te groot (max 500KB).';
      return;
    }
    this._error = null;
    const dataUri = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    await this._save(dataUri);
  }

  async _save(dataUri) {
    this._saving = true; this._saved = false; this._error = null;
    try {
      await api.updateCompanyLogo(dataUri);
      this._logo = dataUri;
      this._saved = true;
      setTimeout(() => { this._saved = false; }, 2500);
    } catch (e) {
      this._error = 'Opslaan mislukt';
    } finally {
      this._saving = false;
    }
  }

  async _remove() {
    const ok = await confirmDialog({
      title: 'Logo verwijderen',
      message: 'Weet je zeker dat je het bedrijfslogo wilt verwijderen?',
      confirmLabel: 'Verwijderen',
      danger: true,
    });
    if (!ok) return;
    await this._save(null);
    this._logo = null;
  }



  render() {
    if (this._loading) return html`<div class="page"><p class="muted">Laden…</p></div>`;
    return html`
      <div class="page">
        <h1>Bedrijfsprofiel</h1>
        <p class="subtitle">Beheer hier de gegevens van je bedrijf, zoals het logo.</p>

        <section class="kaart logo-kaart">
          <div class="logo-box">
            ${this._logo
              ? html`<img src=${this._logo} alt="Bedrijfslogo" class="logo-img">`
              : html`<div class="logo-empty">Nog geen logo</div>`}
          </div>
          <div class="logo-info">
            <h2>Bedrijfslogo</h2>
            <p class="hint" style="margin:4px 0 0;">PNG, JPG, SVG of WebP. Maximaal 500 KB.</p>
            ${this._canEdit ? html`
              <input id="logo-input" type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" @change=${this._onFile} style="display:none;">
              <div class="actions" style="margin-top:12px;">
                <button class="upload" ?disabled=${this._saving} @click=${this._pickFile}>
                  ${this._saving ? 'Uploaden…' : (this._logo ? 'Logo vervangen' : 'Logo uploaden')}
                </button>
                ${this._logo ? html`<button class="remove" ?disabled=${this._saving} @click=${this._remove}>Verwijderen</button>` : ''}
                ${this._error ? html`<span class="err">${this._error}</span>` : ''}
                ${this._saved ? html`<span class="ok">Opgeslagen ✓</span>` : ''}
              </div>
            ` : html`<p class="muted" style="margin:10px 0 0;">Alleen een beheerder of eigenaar kan het logo aanpassen.</p>`}
          </div>
        </section>

      </div>
    `;
  }

  static styles = css`
    :host { display: block; }
    .page { max-width: 704px; }
    h1 { margin: 0 0 0.35rem; font-size: 1.5rem; font-weight: 700; color: var(--mv-text); }
    h2 { margin: 0; font-size: 0.95rem; font-weight: 700; color: var(--mv-text); }
    .subtitle { color: var(--mv-text-muted); margin: 0 0 1.5rem; font-size: 0.9rem; }
    .kaart {
      background: var(--mv-surface, #fff); border: 1px solid var(--mv-border); border-radius: 12px;
      padding: 20px; margin-bottom: 1.5rem; box-sizing: border-box;
    }
    .logo-kaart { display: flex; align-items: center; gap: 20px; }
    .logo-box {
      flex: none; width: 144px; height: 80px; border: 1px solid var(--mv-border); border-radius: 8px;
      display: flex; align-items: center; justify-content: center; overflow: hidden; background: var(--mv-surface, #fff);
    }
    .logo-img { max-width: 85%; max-height: 80%; object-fit: contain; }
    .logo-empty { color: var(--mv-text-muted); font-size: 0.8rem; }
    .logo-info { flex: 1; min-width: 0; }
    .actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    .hint { color: var(--mv-text-muted); font-size: 0.8rem; line-height: 1.45; margin: 0.6rem 0 0; }
    .muted { color: var(--mv-text-muted); }
    .ok { color: var(--mv-primary); font-weight: 600; font-size: 0.85rem; }
    .err { color: #DC2626; font-weight: 600; font-size: 0.85rem; }
    button.upload {
      background: var(--mv-primary); color: #fff; border: none; border-radius: 6px;
      padding: 8px 14px; font-weight: 700; font-size: 0.85rem; cursor: pointer; font-family: inherit;
    }
    button.remove {
      background: transparent; color: var(--mv-text); border: 1px solid var(--mv-border);
      border-radius: 6px; padding: 8px 14px; font-weight: 600; font-size: 0.85rem; cursor: pointer; font-family: inherit;
    }
    button.remove:hover:not(:disabled) { border-color: #DC2626; color: #DC2626; }
    button:disabled { opacity: 0.55; cursor: default; }
  `;
}

customElements.define('company-settings', CompanySettings);
